<template>
  <div
    class="modal fade"
    id="modalReverse"
    ref="modal"
    data-bs-backdrop="static"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="bg-body-secondary modal-header py-2">
          {{ $t('Reverse Record') + ': ' + name }}
          <button type="button" class="btn-close shadow-none" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div
          class="modal-body"
          v-if="loading"
        >
          <placeholder-content :is-load="true" />
        </div>
        <div
          class="modal-body"
          v-else-if="!hasPermission"
        >
          <access-denied />
        </div>
        <div
          class="modal-body"
          v-else
        >
          <div class="alert alert-warning py-2" style="font-size: 13px;">
            <i class="fa-solid fa-rotate-left me-1" />
            {{ $t('Enter the quantity to return to each lot. It cannot exceed the quantity already recorded.') }}
          </div>
          <div class="overflow-x-auto">
            <table class="mb-0 mt-2 table table-sm">
              <thead
                class="table-light"
                style="font-size: 14px;"
              >
                <tr>
                  <th style="min-width: 180px;">{{ $t('Lot No.') }}</th>
                  <th style="min-width: 100px;">{{ $t('Expiry Date') }}</th>
                  <th class="text-end" style="min-width: 110px;">{{ $t('Recorded (Used)') }}</th>
                  <th class="text-end" style="min-width: 120px;">{{ $t('Quantity to Return') }}</th>
                  <th style="min-width: 80px;">{{ $t('Unit') }}</th>
                </tr>
              </thead>
              <tbody style="font-size: 14px;">
                <tr
                  v-for="(row, index) in rows"
                  :key="row.inventory_lot_id"
                >
                  <td class="align-content-center">{{ row.lot_number }}</td>
                  <td class="align-content-center">{{ row.expiry_date? formatDateTime(row.expiry_date): '-' }}</td>
                  <td class="align-content-center text-end">{{ formatDigit(row.currently_used) }}</td>
                  <td>
                    <div class="input-group-sm">
                      <input
                        type="number"
                        v-model.number="row.reverse_quantity"
                        inputmode="decimal"
                        class="form-control shadow-none text-end"
                        :class="{ 'is-invalid': isRowInvalid(row) }"
                        style="font-size: 12px;"
                        :placeholder="$t('Enter quantity') + '...'"
                      >
                    </div>
                  </td>
                  <td class="align-content-center">{{ $t(row.unit) }}</td>
                </tr>
                <tr v-if="rows.length == 0">
                  <td colspan="5" class="text-center">{{ $t('No recorded lots to reverse.') }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="mt-3">
            <label class="form-label mb-1" style="font-size: 13px;">
              {{ $t('Reason for reversal (Required)') }}:
            </label>
            <textarea
              v-model="reason"
              class="form-control shadow-none"
              rows="2"
              style="font-size: 13px;"
              :placeholder="$t('Type your reason here (e.g., Over-recorded, Wrong lot)') + '...'"
            ></textarea>
          </div>
          <div class="text-end mt-2">
            {{ $t('Total to Return') + ': ' }}
            <b>{{ (totalReverse? formatNumberTwoDigit(totalReverse): '0.00') + ' ' + $t(unit) }}</b>
          </div>
        </div>
        <div
          class="modal-footer py-2"
          v-if="!loading"
        >
          <button
            type="button"
            class="btn btn-sm btn-secondary"
            data-bs-dismiss="modal"
          >
            {{ $t('Close') }}
          </button>
          <ladda
            v-if="hasPermission"
            text="Confirm Reversal"
            :btn-class="'btn btn-sm btn-theme'"
            :disabled="!canSubmit"
            @click="handleConfirm"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="js">
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import Ladda from '@/plugins/Ladda.vue';
import AccessDenied from '@/plugins/AccessDenied.vue';
import { InventoryProductionBatches } from '@/services/endpoint/inventory-production-batches.service';
import moment from "moment";
import numeral from 'numeral';

export default {
  props: {
    id: {
      type: Number,
      default: 0
    },
    name: {
      type: String,
      default: ''
    },
    unit: {
      type: String,
      default: ''
    },
  },
  data() {
    return {
      loading: true,
      hasPermission: true,
      rows: [],
      reason: ''
    }
  },
  components: {
    Ladda,
    PlaceholderContent,
    AccessDenied
  },
  methods: {
    formatDigit(value) {
      if ((value < 0.01) && value != 0) {
        return numeral(value).format("0,0.0000");
      }
      return numeral(value).format("0,0.00");
    },
    formatNumberTwoDigit(value) {
      return numeral(value).format("0,0.00");
    },
    formatDateTime(value) {
      return moment(value).format('YYYY-MM-DD HH:mm:ss');
    },
    isRowInvalid(row) {
      if (row.reverse_quantity === '' || row.reverse_quantity === null) return false;
      const qty = parseFloat(row.reverse_quantity);
      return isNaN(qty) || qty < 0 || qty > parseFloat(row.currently_used);
    },
    async fetchData() {
      var queryString = '?' + $.param({
        inventory_production_batch_input_id: this.id
      });
      let self = this;
      // อ้างจาก allocation ที่เบิกไปจริง (endpoint แยกสำหรับคืน) -> คืนได้แม้ lot หมดแล้ว
      await InventoryProductionBatches.getReverseLots(this.$parent.id, queryString).then(res => {
        self.rows = res.data.data.lines.map(function(line) {
          return {
            inventory_production_batch_input_id: self.id,
            inventory_lot_id: line.inventory_lot_id,
            lot_number: line.lot_number,
            expiry_date: line.expiry_date,
            currently_used: line.recorded,
            actual_cost_per_unit: line.cost,
            unit_id: line.unit_id,
            unit: line.unit,
            reverse_quantity: ''
          }
        });
      }).catch(err => {
        if (err?.response?.status === 403) {
          self.hasPermission = false;
          return;
        }
        throw err;
      });
    },
    async handleConfirm() {
      if (!this.canSubmit) {
        return;
      }
      let self = this;
      let reversals = this.rows
        .filter(function(row) {
          return row.reverse_quantity !== '' && parseFloat(row.reverse_quantity) > 0;
        })
        .map(function(row) {
          return {
            inventory_production_batch_input_id: row.inventory_production_batch_input_id,
            inventory_lot_id: row.inventory_lot_id,
            quantity: parseFloat(row.reverse_quantity)
          }
        });
      let reversedTotalCost = this.rows.reduce(function(sum, row) {
        if (row.reverse_quantity === '' || parseFloat(row.reverse_quantity) <= 0) return sum;
        return sum + (parseFloat(row.reverse_quantity) * parseFloat(row.actual_cost_per_unit));
      }, 0);
      let total = (this.totalReverse? this.formatNumberTwoDigit(this.totalReverse): '0.00') + ' ' + this.$t(this.unit);
      let params = {
        reason: this.reason,
        reversals: reversals
      };
      try {
        let title = this.$t("Confirm Reversal");
        let html = this.$t('Are you sure you want to return stock for')
          + ' <b>' + this.name + '</b>?<br><br>'
          + this.$t('Total to Return') + ': <b style="font-size: 1.2em; color: #FF7043;">' + total + '</b>'
          + '<br><br><small>' + this.$t('Inventory will be restored to the original lots immediately.') + '</small>';
        this.$swal({
          icon: "warning",
          iconColor: "#FF7043",
          title: title,
          html: html,
          showCancelButton: true,
          cancelButtonText: this.$t("Review Again"),
          confirmButtonText: this.$t("Yes, Return Now"),
          buttonsStyling: false,
          reverseButtons: true,
          focusCancel: true,
          customClass: {
            confirmButton: "btn btn-theme",
            cancelButton: "btn btn-danger me-2",
          },
          focusConfirm: false,
          showLoaderOnConfirm: true,
          preConfirm: async function () {
            return InventoryProductionBatches.reverseRecord(self.$parent.id, params)
              .then((response) => {
                self.$emit('reverse-usage', {
                  id: self.id,
                  reversed: self.totalReverse,
                  reversedTotalCost: reversedTotalCost
                });
                self.$toast.success("<B>" + self.$t('Success') + "</B>\n" + self.$t('Reverse Record Success!'));
                $(self.$refs.modal).modal('hide');
              })
              .catch((error) => {
                if (error?.response?.status === 403) {
                  self.$swal.update({
                    title: self.$t('Access Denied'),
                    confirmButtonText: '<i class="fas fa-redo me-1"></i>' + self.$t("Try again"),
                    text: self.$t('You do not have permission to access this page.')
                  });
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else if (error?.response?.data?.code === "VALIDATION") {
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else {
                  self.$toast.warn(error?.response?.data?.message);
                }
              })
          },
        });
      } catch (error) {
      }
    },
  },
  async mounted() {
    $(this.$refs.modal).modal('show');

    $(this.$refs.modal).on('shown.bs.modal', async () => {
      this.loading = true;
      try {
        await this.fetchData();
        this.loading = false;
      } catch (err) {
      }
    });

    $(this.$refs.modal).on('hidden.bs.modal', () => {
      this.$parent.modalReverseRecordForm.open = false;
    });
  },
  computed: {
    totalReverse() {
      return this.rows.reduce((sum, row) => {
        if (row.reverse_quantity === '' || parseFloat(row.reverse_quantity) <= 0) return sum;
        return sum + parseFloat(row.reverse_quantity);
      }, 0);
    },
    canSubmit() {
      if (this.reason.trim() === '') return false;
      if (this.totalReverse <= 0) return false;
      return !this.rows.some(row => this.isRowInvalid(row));
    }
  }
}
</script>
